package com.evoapps.evoshift.ui.documents

import com.evoapps.evoshift.widgets.SurfaceCard
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.documents.PickKind
import com.evoapps.evoshift.documents.asPickedFile
import com.evoapps.evoshift.documents.documentDropSpec
import com.evoapps.evoshift.filedrop.FilesDropArea
import com.evoapps.evoshift.filedrop.FileDropTags
import evoshift.composeapp.generated.resources.documents_drop
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.filled.UploadFile
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.documents.rememberDocumentPicker
import com.evoapps.evoshift.domain.model.StaffDocument
import com.evoapps.evoshift.domain.model.StaffDocumentType
import com.evoapps.evoshift.ui.adaptive.ContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.user.formatDay
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.StaffDocumentsViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDateField
import com.evoapps.evoshift.widgets.MissingYear
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoSegmented
import com.evoapps.evoshift.widgets.EvoTopBar
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.delete
import evoshift.composeapp.generated.resources.documents_add
import evoshift.composeapp.generated.resources.documents_add_title
import evoshift.composeapp.generated.resources.documents_delete_confirm
import evoshift.composeapp.generated.resources.documents_delete_title
import evoshift.composeapp.generated.resources.documents_empty
import evoshift.composeapp.generated.resources.documents_expires_on
import evoshift.composeapp.generated.resources.documents_expires_on_hint
import evoshift.composeapp.generated.resources.documents_expiry_hr_records
import evoshift.composeapp.generated.resources.documents_expiry_invalid
import evoshift.composeapp.generated.resources.documents_expiry_records_dates
import evoshift.composeapp.generated.resources.documents_expiry_until
import evoshift.composeapp.generated.resources.documents_intro_mine
import evoshift.composeapp.generated.resources.documents_intro_team
import evoshift.composeapp.generated.resources.documents_medical_hint
import evoshift.composeapp.generated.resources.documents_no_expiry
import evoshift.composeapp.generated.resources.documents_not_available
import evoshift.composeapp.generated.resources.documents_not_signature
import evoshift.composeapp.generated.resources.documents_open
import evoshift.composeapp.generated.resources.documents_title_mine
import evoshift.composeapp.generated.resources.documents_title_of
import evoshift.composeapp.generated.resources.documents_type
import evoshift.composeapp.generated.resources.documents_type_contract
import evoshift.composeapp.generated.resources.documents_type_medical
import evoshift.composeapp.generated.resources.documents_type_ohs
import evoshift.composeapp.generated.resources.documents_type_other
import evoshift.composeapp.generated.resources.documents_uploaded_on
import evoshift.composeapp.generated.resources.documents_uploading
import evoshift.composeapp.generated.resources.documents_upload_action
import org.jetbrains.compose.resources.stringResource
import evoshift.composeapp.generated.resources.documents_queue

/**
 * SH-122: one person's documents at a workplace. The person (from the profile) and the
 * workplace's owner and financial managers (from the employee form) open, upload and remove them.
 */
@Composable
fun StaffDocumentsScreen(
    viewModel: StaffDocumentsViewModel,
    onNavigateBack: () -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val shown = (state.error ?: state.message)?.asString()
    LaunchedEffect(shown) {
        shown?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }
    val picker = rememberDocumentPicker { viewModel.onPicked(it) }
    var deleting by remember { mutableStateOf<StaffDocument?>(null) }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            EvoTopBar(
                title = if (state.isMine || state.personName.isBlank()) stringResource(Res.string.documents_title_mine)
                else stringResource(Res.string.documents_title_of, state.personName),
                onBack = onNavigateBack
            )
        }
    ) { padding ->
        // SH-233: a document dropped anywhere on the screen (web, desktop, Android and ChromeOS)
        // goes the chooser's way; SH-161 F4: several at once get their dialogs one after another.
        FilesDropArea(
            spec = documentDropSpec(PickKind.DOCUMENT).copy(multiple = true),
            dropLabel = stringResource(Res.string.documents_drop),
            onFiles = { files -> viewModel.onPickedMany(files.map { it.asPickedFile() }) },
            modifier = Modifier.padding(padding).fillMaxSize(),
            enabled = state.canView && state.canUpload && state.uploading == null && state.draft == null && !state.isLoading,
            snackbarHostState = snackbarHostState,
            tag = FileDropTags.DOCUMENTS
        ) {
        Box(Modifier.fillMaxSize().contentWidth(ContentMaxWidth)) {
            if (state.isLoading) {
                CircularProgressIndicator(Modifier.align(Alignment.Center))
                return@Box
            }
            Column(
                modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                if (!state.canView) {
                    Text(stringResource(Res.string.documents_not_available), style = MaterialTheme.typography.bodyMedium)
                    return@Column
                }
                Text(
                    stringResource(if (state.isMine) Res.string.documents_intro_mine else Res.string.documents_intro_team),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                if (state.canUpload) {
                    Button(onClick = { picker.pick(pdfOnly = false) }, enabled = state.uploading == null) {
                        Icon(Icons.Default.UploadFile, contentDescription = null)
                        Spacer(Modifier.width(8.dp))
                        Text(stringResource(Res.string.documents_add))
                    }
                }
                state.uploading?.let { (name, progress) ->
                    Text(stringResource(Res.string.documents_uploading, name), style = MaterialTheme.typography.bodySmall)
                    LinearProgressIndicator(progress = { progress }, modifier = Modifier.fillMaxWidth())
                }
                if (state.documents.isEmpty() && state.uploading == null) {
                    Text(stringResource(Res.string.documents_empty), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
                state.documents.forEach { document ->
                    DocumentCard(
                        document = document,
                        opening = state.openingId == document.id,
                        onOpen = { viewModel.open(document) },
                        onDelete = if (state.canDelete(document)) { { deleting = document } } else null
                    )
                }
                Text(
                    stringResource(Res.string.documents_not_signature),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
        }
    }

    state.draft?.let { draft ->
        EvoDialog(
            title = stringResource(Res.string.documents_add_title),
            icon = Icons.Default.UploadFile,
            onDismiss = { viewModel.cancelDraft() },
            dismissOnClickOutside = false,
            confirm = DialogAction(stringResource(Res.string.documents_upload_action), { viewModel.upload() }, enabled = !draft.expiryInvalid)
        ) {
            Text(draft.file.name.orEmpty(), color = Color.White, fontWeight = FontWeight.Bold)
            if (state.queue.isNotEmpty()) {
                Text(stringResource(Res.string.documents_queue, state.queue.size), color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
            }
            DialogSection(stringResource(Res.string.documents_type)) {
                EvoSegmented(
                    options = StaffDocumentType.entries.toList(),
                    selected = draft.type,
                    onSelect = { viewModel.setDraftType(it) },
                    optionLabel = { it.label() }
                )
                if (draft.type == StaffDocumentType.MEDICAL_EXAM) {
                    Text(stringResource(Res.string.documents_medical_hint), color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
                }
            }
            DialogSection(stringResource(Res.string.documents_expires_on)) {
                EvoDateField(
                    value = draft.expiresOn,
                    onValueChange = { viewModel.setDraftExpiry(it) },
                    label = stringResource(Res.string.documents_expires_on_hint),
                    missingYear = MissingYear.Upcoming,
                    imeAction = ImeAction.Done,
                    error = stringResource(Res.string.documents_expiry_invalid).takeIf { draft.expiryInvalid }
                )
                if (draft.type.check != null) {
                    Text(
                        stringResource(if (state.recordsStaffDates) Res.string.documents_expiry_records_dates else Res.string.documents_expiry_hr_records),
                        color = Color.White.copy(alpha = 0.7f),
                        style = MaterialTheme.typography.bodySmall
                    )
                }
            }
        }
    }

    deleting?.let { document ->
        EvoDialog(
            title = stringResource(Res.string.documents_delete_title),
            icon = Icons.Default.Delete,
            onDismiss = { deleting = null },
            destructive = true,
            width = DialogWidth.Compact,
            confirm = DialogAction(stringResource(Res.string.delete), { viewModel.delete(document); deleting = null }, destructive = true)
        ) {
            Text(stringResource(Res.string.documents_delete_confirm, document.fileName), color = Color.White.copy(alpha = 0.85f))
        }
    }
}

@Composable
private fun DocumentCard(document: StaffDocument, opening: Boolean, onOpen: () -> Unit, onDelete: (() -> Unit)?) {
    SurfaceCard(
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.Description, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
                Spacer(Modifier.width(8.dp))
                Text(document.type.label(), fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                if (onDelete != null) {
                    IconButton(onClick = onDelete, modifier = Modifier.size(36.dp)) {
                        Icon(Icons.Default.Delete, contentDescription = stringResource(Res.string.documents_delete_title))
                    }
                }
            }
            Text(document.fileName, style = MaterialTheme.typography.bodyMedium)
            Text(
                document.expiresOn?.let { stringResource(Res.string.documents_expiry_until, formatDay(it)) } ?: stringResource(Res.string.documents_no_expiry),
                style = MaterialTheme.typography.bodySmall
            )
            document.uploadedAt?.let {
                Text(
                    stringResource(Res.string.documents_uploaded_on, formatMoment(it), formatSize(document.sizeBytes)),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            OutlinedButton(onClick = onOpen, enabled = !opening, modifier = Modifier.align(Alignment.End)) {
                if (opening) CircularProgressIndicator(Modifier.size(16.dp), strokeWidth = 2.dp)
                else Text(stringResource(Res.string.documents_open))
            }
        }
    }
}

@Composable
fun StaffDocumentType.label(): String = stringResource(
    when (this) {
        StaffDocumentType.CONTRACT -> Res.string.documents_type_contract
        StaffDocumentType.MEDICAL_EXAM -> Res.string.documents_type_medical
        StaffDocumentType.OHS -> Res.string.documents_type_ohs
        StaffDocumentType.OTHER -> Res.string.documents_type_other
    }
)
